import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentSite } from '@/contexts/SiteContext';
import { SiteSwitcherMenu } from '@/components/layout/SiteSwitcherMenu';
import type { Site } from '@/types';

interface BreadcrumbProps {
  // pageName: the right-hand side of the breadcrumb. Comes from the route
  // metadata map (see lib/pageNames.ts) or from a dynamic page-name override.
  pageName: string;
}

// Breadcrumb is now plain context text ("Site / Page"). The visible site
// switcher lives in the nav rail (SiteWorkspaceNav); the breadcrumb keeps only
// the global Cmd+K shortcut, which opens the same shared SiteSwitcherMenu. The
// shortcut's availability is unchanged from before the rail redesign: it opens
// only on a site-scoped route with more than one switchable site.
export function Breadcrumb({ pageName }: BreadcrumbProps) {
  const { currentSite, allSites, setSite } = useCurrentSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const visibleSites = useMemo<Site[]>(
    () => allSites.filter((s) => s.status !== 'decommissioned'),
    [allSites],
  );
  const canSwitch = visibleSites.length > 1;

  // Cmd+K / Ctrl+K opens the shared switcher. The listener lives here (the
  // breadcrumb is mounted once per protected page) so there's exactly one
  // global handler.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleSelect = useCallback(
    (slug: string) => {
      if (slug !== currentSite?.slug) setSite(slug);
      setOpen(false);
    },
    [currentSite, setSite],
  );

  const handleManageSites = useCallback(() => {
    setOpen(false);
    navigate('/sites');
  }, [navigate]);

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 min-w-0">
      {currentSite ? (
        <>
          <span className="max-w-[14rem] truncate text-sm font-semibold text-dash-primary">
            {currentSite.name}
          </span>
          <span className="text-dash-muted text-sm" aria-hidden="true">
            /
          </span>
          <span className="text-sm text-dash-secondary truncate">{pageName}</span>
        </>
      ) : (
        <span className="text-sm font-semibold text-dash-primary">{pageName}</span>
      )}

      {/* The switcher menu's availability matches the pre-redesign breadcrumb:
          a site is active and there's more than one site to switch to. */}
      {currentSite && canSwitch ? (
        <SiteSwitcherMenu
          open={open}
          onClose={() => setOpen(false)}
          sites={visibleSites}
          currentSlug={currentSite.slug}
          onSelect={handleSelect}
          onManageSites={handleManageSites}
        />
      ) : null}
    </nav>
  );
}
